import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Loader from "components/Progress";
import Chart from "components/Charts/Stacked Bar Chart";
import Card from "@mui/material/Card";
import Nodata from "assets/img/pieChart.png";
import { useGetAlarmsQuery } from "../../../services/alarms";
import {
  setMapPage,
  setListPage,
  setLiveArr,
  setView,
} from "rtkSlices/AssetViewSlice";
import { resetFilter } from "rtkSlices/filterDevicesSlice";
import {  useDispatch } from "react-redux";

const startTime = new Date().setDate(new Date().getDate() - 14);
const endTime = new Date();

export default function Trend({ sensorIds, loading, serviceId }) {
  let priorities = ["CRITICAL", "MAJOR", "MINOR", "WARNING"];
  let statuses = ["ACTIVE", "ACKNOWLEDGED", "CLEARED"];
  let token = window.localStorage.getItem("token");
  const metaDataValue = useSelector((state) => state.metaData);
  const [alarmsData, setAlarmsData] = useState([]);
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  const groupIds = []
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(setMapPage(1));
      dispatch(setListPage(1));
      dispatch(resetFilter());
    };
  }, []);
  metaDataValue?.services.forEach(s=>{
    if(s.group?.id){
      groupIds.push(`${s.id}:${s.group?.id}`)
    }
  })
  // "All Solutions" means "all of MY accessible solutions" — send the array
  // of role-visible service ids. See same helper in List/Count.
  const buildServiceIdParam = () => {
    if (serviceId) return `serviceId=${serviceId}&`;
    if (alarmsFilter.solutions.length && alarmsFilter.solutions[0] !== "All") {
      const id = metaDataValue.services.find((s) => s.name == alarmsFilter.solutions[0])?.id;
      if (id) return `serviceId=${id}&`;
      // Selection no longer in the user's accessible services (deleted/
      // renamed mid-session): fall through to the "All" branch — never emit
      // an unscoped request, which would silently widen the scope past the
      // user's RBAC. The parent's stale-selection guard resets the dropdown.
    }
    return metaDataValue.services?.length
      ? `serviceId=${JSON.stringify(metaDataValue.services.map((s) => s.id))}&`
      : "";
  };
  const serviceIdParam = buildServiceIdParam();
  const chartRes = useGetAlarmsQuery({
    token,
    // pageSize=2000 (MAX_PAGE_SIZE): the trend aggregates the whole two-week
    // window client-side. Without an explicit pageSize the serviceId-scoped
    // backend path defaults to 10 (an OOM guard), so the chart silently
    // reflected only the 10 most-recently-updated alarms.
    // dashboard=true windows the query on createdAt (alarm-raised time) instead
    // of updatedAt — so a long-lived alarm re-raised inside the window (e.g. a
    // chronic signal alarm) no longer consumes a 2000-row slot only to be dropped
    // by the creationTime window filter below. createdAt (~creationTime) is also
    // the correct axis for this "past two weeks" trend, matching the bucketing.
    params: `?pageSize=2000&currentPage=1&dashboard=true&${
      alarmsFilter.priority.length
        ? `priority=${JSON.stringify(alarmsFilter.priority)}&`
        : ""
    }${serviceIdParam}${
      alarmsFilter.status.length
        ? `status=${JSON.stringify(alarmsFilter.status)}&`
        : ""
    }${alarmsFilter.search.rule ? `type=${alarmsFilter.search.rule}&` : ""}${
      alarmsFilter.search.asset ? `source=${JSON.stringify(sensorIds)}&` : ""
    }dateFrom=${new Date(
      new Date(startTime).setSeconds(0)
    ).toISOString()}&dateTo=${new Date(
      new Date(endTime).setSeconds(0)
    ).toISOString()}${
      alarmsFilter.emails ? `emails=${alarmsFilter.emails}&` : ""
    }${alarmsFilter.actuations ? `actuations=${alarmsFilter.actuations}` : ""}${(alarmsFilter.solutions.length && alarmsFilter.solutions[0] != "All" && metaDataValue.services.find(s=>s.name == alarmsFilter.solutions[0]).group?.id) || (serviceId) ? `&groupId=${metaDataValue.services.find(s=> serviceId ? (s.id == serviceId) : (s.name == alarmsFilter.solutions[0])).group?.id}` : groupIds.length ? `&groupId=${JSON.stringify(groupIds)}`: ``}`,
  }, { skip: loading });

  useEffect(() => {
    if (chartRes.isSuccess) {
      let data = chartRes.data.payload.data;
      if (data && data.length) {
        getChartData(data);
      } else {
        setAlarmsData([]);
      }
    }
  }, [chartRes.isFetching]);

  function getChartData(data) {
    let temp = [];
    data.forEach((d) => {
      const created = new Date(d.creationTime);
      // Bucket + sort on the actual calendar day (local midnight), NOT the
      // formatted label. The API returns alarms ordered by updatedAt, so the
      // old `temp.reverse()` only flipped encounter-order and left the x-axis
      // out of chronological order.
      const dayKey = new Date(
        created.getFullYear(),
        created.getMonth(),
        created.getDate()
      ).getTime();
      // en-GB (DD/MM/YYYY) to match the app-wide convention and drop the
      // browser-locale-dependent US M/D/YYYY the default toLocaleDateString gave.
      const dayLabel = created.toLocaleDateString("en-GB");
      let ind = temp.findIndex((t) => t.dayKey == dayKey);
      if (ind != -1) {
        temp[ind][d.severity] = temp[ind][d.severity]
          ? temp[ind][d.severity] + d.count
          : d.count;
      } else {
        temp.push({
          dayKey,
          time: dayLabel,
          [d.severity]: d.count,
        });
      }
    });
    temp.forEach((t) => {
      priorities.forEach((p) => {
        if (!t[p]) {
          t[p] = 0;
        }
      });
    });
    // Safety net: drop buckets whose creation day is before the two-week window.
    // With dashboard=true the query windows on createdAt (~creationTime), so this
    // normally drops nothing; it still guards the rare createdAt/creationTime skew
    // (e.g. a back-dated alarm) from rendering a bar left of "two weeks ago".
    const windowStart = new Date(startTime);
    const windowStartDay = new Date(
      windowStart.getFullYear(),
      windowStart.getMonth(),
      windowStart.getDate()
    ).getTime();
    temp = temp.filter((t) => t.dayKey >= windowStartDay);
    temp.sort((a, b) => a.dayKey - b.dayKey);
    setAlarmsData(temp);
  }

  return (
    <Card
      style={{
        minHeight: "265px",
        verticalAlign: "middle",
        position: "relative",
        width: "100%",
        padding: "16px",
        margin: "10px 0px 16px 0px",
      }}
    >
      <p
        style={{
          color: "#bfbec8",
          fontSize: "15px",
        }}
      >
        <b>{`Trend ( past two weeks )`}</b>
      </p>
      {chartRes.isFetching || loading ? (
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Loader />
        </span>
      ) : alarmsData.length ? (
        <Chart data={alarmsData} name="Alarms" />
      ) : (
        <div
          style={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            color: "#c8c8c8",
          }}
        >
          <img src={Nodata} height="100px" width="100px" />
          <p>No Alarms Found</p>
        </div>
      )}
    </Card>
  );
}
